/**
 * SDK Verify Page
 * ================
 * Standalone page for SDK verification flow.
 *
 * Access via: /sdk/verify?token=sdk_xxx
 * Embed tenants get /sdk/verify?code=xxx instead: a one-time code (2 min)
 * that this page exchanges for the token, so the token never sits in a URL.
 *
 * This page can be opened in an iframe or new window by customers.
 */

import React, { useMemo, useEffect, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import VerificationSDK from './VerificationSDK';

export default function SDKVerifyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Handle malformed URLs where user uses ?token=xxx?reset=true instead of ?token=xxx&reset=true
  useEffect(() => {
    const fullUrl = window.location.href;
    // Check if there's a malformed URL with double question marks
    if (fullUrl.includes('?') && fullUrl.split('?').length > 2) {
      // Fix the URL by replacing the second ? with &
      const [base, ...params] = fullUrl.split('?');
      const fixedUrl = base + '?' + params.join('&');
      window.history.replaceState(null, '', fixedUrl);
      // Reload search params
      const newParams = new URLSearchParams(params.join('&'));
      const token = newParams.get('token');
      const reset = newParams.get('reset');
      if (reset === 'true' && token) {
        setSearchParams({ token, reset: 'true' });
      }
    }
  }, [location, setSearchParams]);

  const urlToken = searchParams.get('token');
  const code = searchParams.get('code');
  const [codeToken, setCodeToken] = useState(null);
  const [codeState, setCodeState] = useState(code && !urlToken ? 'exchanging' : 'idle');
  const token = urlToken || codeToken;
  const theme = searchParams.get('theme') || 'light';
  const reset = searchParams.get('reset') === 'true';

  // Determine API base URL from current environment
  // Include reset parameter if specified
  const baseUrl = useMemo(() => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    let sdkUrl;

    // Handle case where apiBase already includes /api/v1
    if (apiBase.endsWith('/api/v1')) {
      sdkUrl = `${apiBase}/sdk`;
    } else {
      // Remove trailing slash if present
      const cleanBase = apiBase.replace(/\/$/, '');
      sdkUrl = `${cleanBase}/api/v1/sdk`;
    }

    // Add reset parameter if specified (will be included in all SDK API calls)
    if (reset) {
      sdkUrl += '?reset=true';
    }

    return sdkUrl;
  }, [reset]);

  // Exchange the one-time code for the token, then drop the code from the URL.
  useEffect(() => {
    if (!code || urlToken) return undefined;
    let cancelled = false;
    const exchangeUrl = `${baseUrl.split('?')[0]}/exchange-code`;
    fetch(exchangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => {
        if (cancelled) return;
        if (!data?.access_token) throw new Error('no token');
        setCodeToken(data.access_token);
        setCodeState('done');
        const next = new URL(window.location.href);
        next.searchParams.delete('code');
        window.history.replaceState(null, '', next.pathname + next.search);
      })
      .catch(() => {
        if (!cancelled) setCodeState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [code, urlToken, baseUrl]);

  const handleComplete = (result) => {
    console.log('Verification complete:', result);

    // If there's a redirect URL, the SDK component handles it
    // Otherwise, show a success message or close the window
    if (!result?.redirect_url) {
      // Post message to parent window (for iframe integration)
      if (window.parent !== window) {
        window.parent.postMessage(
          { type: 'getclearance:complete', result },
          '*'
        );
      }
    }
  };

  const handleError = (error) => {
    console.error('Verification error:', error);

    // Post message to parent window
    if (window.parent !== window) {
      window.parent.postMessage(
        { type: 'getclearance:error', error: error.message },
        '*'
      );
    }
  };

  if (!token && codeState === 'exchanging') {
    return <div className={`sdk-page ${theme}`} aria-busy="true" />;
  }

  if (!token) {
    const expired = codeState === 'failed';
    return (
      <div className="sdk-page">
        <div className="sdk-page-error">
          <h1>{expired ? 'Link expired' : 'Missing Token'}</h1>
          <p>
            {expired
              ? 'This verification link has expired or was already used. Start again from the app.'
              : 'No verification token provided. Please use the URL provided by your application.'}
          </p>
        </div>
        <style>{`
          .sdk-page {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
          .sdk-page-error {
            text-align: center;
            padding: 48px;
            background: white;
            border-radius: 16px;
            box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
            max-width: 400px;
          }
          .sdk-page-error h1 {
            color: #ef4444;
            margin: 0 0 16px;
          }
          .sdk-page-error p {
            color: #6b7280;
            margin: 0;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className={`sdk-page ${theme}`}>
      <VerificationSDK
        accessToken={token}
        baseUrl={baseUrl}
        onComplete={handleComplete}
        onError={handleError}
        theme={theme}
      />
      <style>{`
        .sdk-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        }
        .sdk-page.dark {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        }
        @media (max-width: 480px) {
          .sdk-page {
            padding: 0;
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}
